import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

import { Button } from "../base/Button";
import { IDamagePreviewResult } from "../../models/Category/TitleConfig";

interface DamageDescriptionPreviewProps {
  enabled: boolean;
  result: IDamagePreviewResult | undefined;
  isGenerating: boolean;
  onGenerate: () => void;
}

export default function DamageDescriptionPreview({
  enabled,
  result,
  isGenerating,
  onGenerate,
}: DamageDescriptionPreviewProps) {
  return (
    <section className="bg-white rounded shadow-sm border border-gray-200 p-4">
      <div className="flex items-start justify-between gap-2 mb-3">
        <h2 className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
          Damage Description Preview
        </h2>
        <span className="rounded border border-gray-200 bg-gray-50 px-2 py-0.5 text-[11px] text-gray-600">
          Example Only
        </span>
      </div>

      {!enabled ? (
        <p className="text-sm text-gray-500">
          Turn on the category-specific damage prompt to preview a condition
          description.
        </p>
      ) : (
        <>
          <Button
            fullWidth
            size="sm"
            onClick={onGenerate}
            disabled={isGenerating}
          >
            {isGenerating ? "Generating…" : "Generate Example Description"}
          </Button>

          {result && (
            <>
              <div className="mt-3 flex items-start gap-2 rounded border border-green-200 bg-green-50 p-3">
                <p className="flex-1 text-sm">{result.conditionDescription}</p>
                <CheckCircleIcon className="h-5 w-5 shrink-0 text-green-600" />
              </div>

              {result.observations.length > 0 && (
                <ul className="mt-2 list-disc space-y-0.5 pl-5">
                  {result.observations.map((observation) => (
                    <li key={observation} className="text-xs text-gray-600">
                      {observation}
                    </li>
                  ))}
                </ul>
              )}

              {result.warnings.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {result.warnings.map((warning) => (
                    <li
                      key={warning}
                      className="flex gap-1.5 text-xs text-amber-700"
                    >
                      <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
                      <span>{warning}</span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          <p className="mt-3 text-xs text-gray-500">
            Generated from the prompt alone with no photos, so it shows the
            wording and tone rather than a real assessment. Live parts in Core
            are described from their own images.
          </p>
        </>
      )}
    </section>
  );
}
